import { useRef } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { QuoteSection } from "@/components/site/QuoteSection";
import { SERVICES, STEPS, getService } from "@/lib/services";
import { SITE, EASE } from "@/lib/site";

const ServiceHero = ({ s }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  return (
    <section ref={ref} data-testid="service-hero" className="grain relative flex min-h-[88svh] items-end overflow-hidden bg-ink text-paper">
      <motion.div className="absolute inset-0" style={{ y }}>
        <motion.img key={s.slug} src={s.image} alt={s.title} className="h-full w-full object-cover"
          initial={{ scale: 1.3, clipPath: "inset(0 0 100% 0)" }} animate={{ scale: 1.05, clipPath: "inset(0 0 0% 0)" }} transition={{ duration: 1.6, ease: EASE }} />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/60" />
      </motion.div>
      <div className="relative mx-auto w-full max-w-[1400px] px-5 pb-14 pt-36 sm:px-8">
        <p className="eyebrow text-gold mb-6">{s.num} — Nos services · {SITE.city} & alentours</p>
        <h1 key={s.slug} className="font-display text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em]" data-testid="service-title">
          <MaskLines onLoad delay={0.5} lines={[s.lines[0], <em key="e" className="text-gold">{s.lines[1]}</em>]} />
        </h1>
        <p className="mt-8 max-w-xl text-lg text-paper/85">{s.short}</p>
      </div>
    </section>
  );
};

export default function ServicePage() {
  const { slug } = useParams();
  const s = getService(slug);
  if (!s) return <Navigate to="/" replace />;
  const others = SERVICES.filter((o) => o.slug !== s.slug);
  return (
    <main data-testid={`service-page-${s.slug}`}>
      <ServiceHero s={s} />
      <section className="bg-paper px-5 py-24 sm:px-8 lg:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="eyebrow text-gold-deep mb-6 flex items-center gap-4">
              <span className="flex gap-2">{[s.icon, s.icon2].filter(Boolean).map((ic) => <img key={ic} src={ic} alt="" className="h-14 w-auto" />)}</span>
              Notre approche
            </p>
            <Reveal><p className="font-display text-3xl sm:text-4xl leading-[1.2]">{s.intro}</p></Reveal>
            <ul className="mt-12 grid gap-px border border-paper-line bg-paper-line sm:grid-cols-2" data-testid="service-items">
              {s.items.map((it, i) => (
                <Reveal key={it} delay={i * 0.05} className="flex items-center gap-4 bg-paper p-5">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink text-gold"><Check className="h-4 w-4" /></span>
                  <span className="font-medium">{it}</span>
                </Reveal>
              ))}
            </ul>
          </div>
          <Reveal className="lg:col-span-5">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img src={s.detailImage} alt={s.title} className="h-full w-full object-cover transition-transform duration-[1.5s] hover:scale-105" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-paper-deep px-5 py-24 sm:px-8 lg:py-32" data-testid="service-process">
        <div className="mx-auto max-w-[1400px]">
          <p className="eyebrow text-gold-deep mb-6">Déroulement</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-[1.02]"><MaskLines lines={["Cinq étapes,", <em key="e">zéro mauvaise surprise.</em>]} /></h2>
          <ol className="mt-14 grid gap-8 md:grid-cols-5">
            {STEPS.map(([t, d], i) => (
              <Reveal key={t} delay={i * 0.08} className="border-t border-ink pt-6">
                <span className="font-display text-5xl text-gold-deep">0{i + 1}</span>
                <h3 className="mt-4 font-display text-2xl leading-tight">{t}</h3>
                <p className="mt-3 text-sm text-slate-600">{d}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <QuoteSection defaultType={s.slug} title={["Votre devis", <em key="e" className="text-gold">{s.label.toLowerCase()} gratuit.</em>]} />

      <section className="bg-paper px-5 py-20 sm:px-8">
        <div className="mx-auto max-w-[1400px]">
          <p className="eyebrow text-gold-deep mb-8">Nos autres services</p>
          <div className="grid gap-4 md:grid-cols-2">
            {others.map((o) => (
              <Link key={o.slug} to={`/services/${o.slug}`} data-testid={`other-service-${o.slug}`} className="group flex items-center justify-between border border-paper-line p-7 transition-colors duration-500 hover:bg-ink hover:text-paper">
                <span><span className="font-mono text-xs text-gold-deep group-hover:text-gold">{o.num}</span><span className="block font-display text-3xl">{o.title}</span></span>
                <ArrowUpRight className="h-6 w-6 transition-transform duration-500 group-hover:rotate-45" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
